import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEditor, EditorContent } from '@tiptap/react';

import { toggleCommentsSidebar } from '../../modules/app';
import { setActiveInlineComment } from '../../modules/comment';

import { buildSceneExtensions, parseSceneDoc } from './tiptapExtensions';
import {
  CommentDecorations,
  REFRESH_COMMENT_DECORATIONS,
} from './commentDecorations';
import {
  audioFeedbackSelectionFromRange,
  rangeToAnchor,
} from './sceneCommentMapping';
import SceneReactionsToolbar from './sceneReactionsToolbar';
import {
  AudioHighlightDecorations,
  REFRESH_AUDIO_HIGHLIGHT,
} from './audioHighlightDecorations';

/**
 * One read-only scene, with comment/reaction decorations projected on top.
 * Reports text selections upward so the parent can show the reaction toolbar.
 */
const SceneReadView = ({
  scene,
  comments,
  activeCommentId,
  allowComments,
  onCommentClick,
  onSelect,
  onClearSelection,
  audioHighlight,
  audioFeedbackDockActive,
  onAudioFeedbackSelectionChange,
}) => {
  // The decoration plugin reads these through refs so it always sees the
  // latest Redux state without being rebuilt.
  const commentsRef = useRef(comments);
  commentsRef.current = comments;
  const activeRef = useRef(activeCommentId);
  activeRef.current = activeCommentId;
  const audioHighlightRef = useRef(audioHighlight);
  audioHighlightRef.current = audioHighlight;
  const audioFeedbackDockActiveRef = useRef(audioFeedbackDockActive);
  audioFeedbackDockActiveRef.current = audioFeedbackDockActive;
  const audioFeedbackSelectionChangeRef = useRef(
    onAudioFeedbackSelectionChange
  );
  audioFeedbackSelectionChangeRef.current = onAudioFeedbackSelectionChange;

  const editor = useEditor(
    {
      editable: false,
      extensions: [
        ...buildSceneExtensions(),
        CommentDecorations({
          sceneId: scene.id,
          getComments: () => commentsRef.current,
          getActiveCommentId: () => activeRef.current,
          onCommentClick,
        }),
        AudioHighlightDecorations({
          sceneId: scene.id,
          getAnchor: () => audioHighlightRef.current,
        }),
      ],
      content: parseSceneDoc(scene.content),
      onSelectionUpdate: ({ editor: activeEditor }) => {
        if (!allowComments) return;
        const { from, to } = activeEditor.state.selection;
        if (from === to) {
          audioFeedbackSelectionChangeRef.current?.(null);
          onClearSelection();
          return;
        }
        const anchor = rangeToAnchor(activeEditor.getJSON(), from, to);
        if (!anchor) {
          audioFeedbackSelectionChangeRef.current?.(null);
          onClearSelection();
          return;
        }
        if (audioFeedbackDockActiveRef.current) {
          const text = activeEditor.state.doc.textBetween(from, to, ' ');
          audioFeedbackSelectionChangeRef.current?.(
            audioFeedbackSelectionFromRange(
              activeEditor.getJSON(),
              scene.id,
              from,
              to,
              text
            )
          );
          onClearSelection();
          return;
        }
        const start = activeEditor.view.coordsAtPos(from);
        onSelect({
          sceneId: scene.id,
          anchor,
          coords: {
            top: start.top,
            left: start.left,
            x: start.left,
            y: start.top,
          },
        });
      },
    },
    [scene.id]
  );

  // Rebuild decorations when the comment list or active comment changes.
  useEffect(() => {
    if (!editor) return;
    editor.view.dispatch(
      editor.state.tr.setMeta(REFRESH_COMMENT_DECORATIONS, true)
    );
  }, [editor, comments, activeCommentId]);

  useEffect(() => {
    if (!editor) return;
    editor.view.dispatch(
      editor.state.tr.setMeta(REFRESH_AUDIO_HIGHLIGHT, true)
    );
  }, [editor, audioHighlight]);

  return <EditorContent editor={editor} className="scene-read-view br-text" />;
};

/**
 * Read-only renderer for a chapter of a 'tiptap'-format book: a vertical list
 * of scenes with scene-break dividers, inline comments/reactions as
 * decorations, and selection-to-comment creation — the scene-aware counterpart
 * to the Draft reader path in containers/content/reader/chapter/textSegment.js.
 */
const SceneChapterReader = ({
  chapter,
  comments,
  allowComments,
  onInitialized,
  audioHighlight,
  audioFeedbackDockActive = false,
  onAudioFeedbackSelectionChange,
}) => {
  const dispatch = useDispatch();
  const activeInlineComment = useSelector(
    state => state.comment.activeInlineComment
  );

  const [selection, setSelection] = useState(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      onInitialized && onInitialized();
    }
  }, [onInitialized]);

  const scenes = Array.isArray(chapter.scenes) ? chapter.scenes : [];

  const onCommentClick = useCallback(
    commentId => {
      dispatch(toggleCommentsSidebar(true));
      dispatch(setActiveInlineComment({ commentId, show: 'sidebar' }));
    },
    [dispatch]
  );

  const clearSelection = useCallback(() => setSelection(null), []);

  return (
    <div className="scene-chapter-reader">
      {scenes.map((scene, index) => (
        <div key={scene.id || index} className="scene-block">
          {index > 0 && (
            <div className="scene-break" aria-hidden>
              <span>* * *</span>
            </div>
          )}
          <SceneReadView
            scene={scene}
            comments={comments}
            activeCommentId={activeInlineComment}
            allowComments={allowComments}
            onCommentClick={onCommentClick}
            onSelect={setSelection}
            onClearSelection={clearSelection}
            audioHighlight={audioHighlight}
            audioFeedbackDockActive={audioFeedbackDockActive}
            onAudioFeedbackSelectionChange={onAudioFeedbackSelectionChange}
          />
        </div>
      ))}

      {selection && !audioFeedbackDockActive && (
        <SceneReactionsToolbar
          sceneId={selection.sceneId}
          anchor={selection.anchor}
          coords={selection.coords}
          onClose={clearSelection}
        />
      )}
    </div>
  );
};

export default SceneChapterReader;
